import React, { useEffect, useState } from 'react';
import DeleteIcon from '../../../components/icons/delete-icon';
import SelectField from '../../../components/Input/select-field';

interface Props {
  readOnly: boolean;
  projects: any[];
  selectedProjects: any[];
  showSpecificProjects: boolean;
  controllers: any[];
  checkedResources: string[];
  onChangeResourcesList: (e: any) => void;
  onAddResourcesList: (resourceIds: string[]) => void;
  onRemoveResourcesList: (resourceIds: string[]) => void;
  onAddProject: (project: any) => void;
  onRemoveProject: (project: any) => void;
  setPermission: (project: any, permission: string, value: boolean) => void;
}

const ResourcePermissions: React.FC<Props> = ({
    readOnly,
    projects,
    selectedProjects,
    showSpecificProjects,
    controllers,
    checkedResources,
    onChangeResourcesList,
    onAddResourcesList,
    onRemoveResourcesList,    
    onAddProject,
    onRemoveProject,
    setPermission
}) => {
  const [selectedProjectId, setSelectedProjectId] = useState<any>(null);

  useEffect(() => {
    if (projects?.length > 0) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects?.length]);

  const projectOptions = projects?.map((project: any) => {
    return { name: project.name, value: project.id };
  });

  const onProjectChange = (projectId: any) => {
    setSelectedProjectId(projectId);
  };

  const addProject = (e: any) => {
    e.preventDefault();    
    if (selectedProjects.some((p) => p.id == selectedProjectId)) {
        return;
    }
    const project = projects.find((project: any) => project.id == selectedProjectId);
    onAddProject(project);
  };

  const onChangeContoller = (e: any, controller: any) => {
        const checked = e.target.checked;
        const controllerResourceIds = controller.resources.map((r: any) => r.id);
        if (checked) {
            onAddResourcesList(controllerResourceIds);
        } else {
            onRemoveResourcesList(controllerResourceIds);
        }
    };

  const controllerChecked = (controller: any) => {        
        return controller.resources.every((resource: any) => {
            return checkedResources.includes(resource.id);
        });
    };

    const allSelected = controllers?.every((controller: any) => controllerChecked(controller));

    const onSelectAll = (e: any) => {
        e.preventDefault();
        const allResourceIds = controllers.flatMap(c => c.resources.map((r:any) => r.id));
        if (allSelected) {
            onRemoveResourcesList(allResourceIds);
            return;
        }

        onAddResourcesList(allResourceIds);
    };

    const onSelectAllReadOnly = (e: any) => {
        e.preventDefault();
        const allResourceIds = controllers.flatMap(c => c.resources.filter((r:any) => r.readOnly).map((r:any) => r.id));
        onAddResourcesList(allResourceIds);
    };

    const onSelectControllerReadOnly = (e: any, controller: any) => {
        e.preventDefault();
        const allResourceIds = controller.resources.filter((r:any) => r.readOnly).map((r:any) => r.id);
        onAddResourcesList(allResourceIds);
    };
   

  return (
    <div>
        <div className="field mt-2">        
            <label className="label">Resource Permissions</label>
            <button className='button is-small mb-2 mr-2' onClick={onSelectAll} disabled={readOnly}>Select {allSelected ? 'None' : 'All'}</button>
            <button className='button is-small mb-2' onClick={onSelectAllReadOnly} disabled={readOnly}>Select All Read Only</button>
            <div className="control">
                <div className="resource-list">
                {(controllers || []).map((controller: any, controllerIndex: number) => (
                    <div key={controllerIndex} className="resource-permission">
                        <div className='resource-permission-header'>
                            <label>
                                <input type="checkbox" checked={controllerChecked(controller)} onChange={(e) => onChangeContoller(e, controller)} disabled={readOnly}></input> {controller.name}
                            </label>
                        </div>
                        
                        {(controller.resources || []).map((resource: any, index: number) => (
                            <div key={index}>
                                <label>
                                    <input type="checkbox" value={resource.id} onChange={(e) => onChangeResourcesList(e)} checked={checkedResources.includes(resource.id) ? true : false} disabled={readOnly}></input>&nbsp;{resource.name}
                                </label>
                            </div>
                        ))}
                        <button className='button is-small mt-2' onClick={(e) => onSelectControllerReadOnly(e, controller)} disabled={readOnly}>Select Read Only</button>
                    </div>
                ))}
                </div>
            </div>          
        </div>

    {showSpecificProjects && (
        <div>
        <label className="label">Specific Project Permissions</label>
        {!readOnly && (
            <div className='select-project-permissions'>
            <SelectField values={projectOptions} onChange={onProjectChange} value={selectedProjectId} />
            <button className='button' onClick={addProject}>Add</button>
            </div>
        )}

        <div>
            <table className='table permissions-table'>
            <thead>
                <tr>
                <th>Project</th>
                <th colSpan={2} className="has-text-centered">View</th>
                <th colSpan={2} className="has-text-centered">Update</th>
                <th></th>
                </tr>
                <tr>
                <th></th>
                <th className="allow-deny-column has-text-centered">Allow</th>
                <th className="allow-deny-column has-text-centered">Deny</th>
                <th className="allow-deny-column has-text-centered">Allow</th>
                <th className="allow-deny-column has-text-centered">Deny</th>
                <th></th>
                </tr>
            </thead>
            <tbody>
                {(selectedProjects || []).map((project: any) => (
                <tr key={project.id}>
                    <td className='project-column'>{project.name}</td>
                    <td className="has-text-centered">
                    <input 
                        type="radio" 
                        name={`${project.id}-view`} 
                        onClick={() => setPermission(project, "view", true)} 
                        checked={project.view} 
                        disabled={readOnly}
                    />
                    </td>
                    <td className="has-text-centered">
                    <input 
                        type="radio" 
                        name={`${project.id}-view`} 
                        onClick={() => setPermission(project, "view", false)} 
                        checked={!project.view} 
                        disabled={readOnly}
                    />
                    </td>
                    <td className="has-text-centered">
                    <input 
                        type="radio" 
                        name={`${project.id}-update`} 
                        onClick={() => setPermission(project, "update", true)} 
                        checked={project.update} 
                        disabled={readOnly}
                    />
                    </td>
                    <td className="has-text-centered">
                    <input 
                        type="radio" 
                        name={`${project.id}-update`} 
                        onClick={() => setPermission(project, "update", false)} 
                        checked={!project.update} 
                        disabled={readOnly}
                    />
                    </td>
                    <td>
                    {!readOnly && (
                        <button className='button is-small' onClick={() => onRemoveProject(project)}>
                        <DeleteIcon />
                        </button>
                    )}
                    </td>
                </tr>
                ))}
            </tbody>
            </table>
        </div>
        </div>)}
    </div>
  );
};

export default ResourcePermissions;